// The guild screen when you are in no guild: FIND (ask by tag, the guilds nearby) · INVITATIONS (accept / decline,
// your own requests) · FOUND (name, tag, description → guild/create).
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { ScreenHero } from "@/kit/city";
import { IconInfo } from "@/kit/icons";
import { Button, Card, CardHead, Disc, Empty, Field, Medallion, Row, RowText, Seg } from "@/kit/primitives";
import { useNearbyGuilds } from "@/state/socialQueries";
import { fmtNum, plural } from "@/game/format";
import { guildLimits, noGuildOf, validateGuildForm } from "@/game/guild";
import { GuildPeek } from "./GuildPeek";

type Tab = "find" | "invites" | "create";

export function NoGuild() {
  const { v, wid, vid, actions } = useWorldCity();
  const [params, setParams] = useSearchParams();
  const tab = (["find", "invites", "create"].includes(params.get("tab") ?? "") ? params.get("tab") : "find") as Tab;
  const setTab = (t: Tab) => setParams(t === "find" ? {} : { tab: t }, { replace: true });
  const ng = noGuildOf(v);
  const invites = ng?.invites ?? [];
  const requests = ng?.requests ?? [];
  const requested = new Set(requests.map((r) => r.gid));
  const limits = guildLimits(v);
  const nearby = useNearbyGuilds(wid, vid, tab === "find");
  const [peek, setPeek] = useState<{ gid: number; tag?: string } | null>(null);
  const [tag, setTag] = useState("");
  const run = (label: string, fn: () => Promise<unknown>, success: string) => actions.run(label, fn, { success });

  return (
    <div className="stack">
      <ScreenHero
        title="No guild yet"
        blurb="A guild shares colours on the map, allies at the gate and a hall to talk in. Ask to join one nearby, answer an invitation, or found your own."
        art={art.hero.guild}
        artWidth="48%"
        artPos="60% 40%"
        stats={[
          { label: "Invitations", value: fmtNum(invites.length), tone: invites.length ? "green" : undefined },
          { label: "Your requests", value: limits.sendRequests ? `${requests.length} / ${limits.sendRequests}` : fmtNum(requests.length) },
          { label: "Nearby", value: nearby.data ? fmtNum(nearby.data.length) : "—" },
        ]}
      />
      <Seg value={tab} onChange={setTab} options={[{ value: "find", label: "Find" }, { value: "invites", label: "Invitations", badge: invites.length || undefined }, { value: "create", label: "Found" }]} />

      {tab === "find" && (
        <>
          <Card>
            <CardHead title="Ask to join by tag" aside={limits.sendRequests ? `${requests.length} of ${limits.sendRequests} requests out` : undefined} />
            <form className="field-row" onSubmit={(e) => { e.preventDefault(); const t = tag.trim(); if (!t) return; void run("request-tag", () => api.guild.addRequest({ wid, vid: vid! }, null, t), `You asked to join [${t.toUpperCase()}].`).then((r) => r && setTag("")); }}>
              <input className="input" value={tag} placeholder="Guild tag" aria-label="Guild tag" autoComplete="off" autoCapitalize="characters" maxLength={6} onChange={(e) => setTag(e.target.value)} />
              <Button type="submit" kind="crimson" size="md" disabled={!tag.trim() || (limits.sendRequests !== null && requests.length >= limits.sendRequests)} busy={actions.isBusy("request-tag")}>Request</Button>
            </form>
          </Card>
          <Card className="gd-nearby">
            <CardHead title="Guilds nearby" aside={nearby.data ? plural(nearby.data.length, "guild") : undefined} />
            {nearby.isLoading && <Empty><span className="spin" /> Looking around…</Empty>}
            {nearby.isError && <Empty>Couldn't read the guilds nearby. <Button size="sm" onClick={() => void nearby.refetch()}>Try again</Button></Empty>}
            {nearby.data && !nearby.data.length && <Empty>No guild holds cities near yours yet.</Empty>}
            <div className="rows">
              {(nearby.data ?? []).map((g, i) => (
                <Row key={g.gid}>
                  <span className="rank">{i + 1}</span>
                  <Medallion src={art.social.crest} alt="" size={56} />
                  <RowText line={<span className="gd-name"><span className="nm">{g.gname}</span></span>} meta={<><b className="gold">[{g.gtag}]</b> · {plural(g.memberCount, "member")} · {fmtNum(g.points)} pts</>} />
                  <div className="gd-acts">
                    <Disc size="sm" label={`About ${g.gname}`} onClick={() => setPeek({ gid: g.gid, tag: g.gtag })}><IconInfo /></Disc>
                    <Button size="sm" kind={requested.has(g.gid) ? "steel" : "gold"} disabled={requested.has(g.gid) || (limits.sendRequests !== null && requests.length >= limits.sendRequests)} busy={actions.isBusy(`request-${g.gid}`)} onClick={() => void run(`request-${g.gid}`, () => api.guild.addRequest({ wid, vid: vid! }, g.gid, g.gtag), `You asked to join ${g.gname}.`)}>
                      {requested.has(g.gid) ? "Asked" : "Request"}
                    </Button>
                  </div>
                </Row>
              ))}
            </div>
          </Card>
        </>
      )}

      {tab === "invites" && (
        <>
          <Card>
            <CardHead title="Invitations" badge={invites.length || undefined} aside={limits.receiveInvites ? `${invites.length} of ${limits.receiveInvites}` : undefined} />
            {!invites.length && <Empty>No guild has invited you yet. Ask to join one under Find.</Empty>}
            <div className="rows">
              {invites.map((inv) => (
                <Row key={inv.gid} className="unread">
                  <Medallion src={art.social.crest} alt="" size={56} />
                  <RowText line={<span className="gd-name"><span className="nm">{inv.gname}</span></span>} meta={inv.gtag ? <b className="gold">[{inv.gtag}]</b> : "invites you"} />
                  <div className="gd-acts">
                    <Disc size="sm" label={`About ${inv.gname}`} onClick={() => setPeek({ gid: inv.gid, tag: inv.gtag })}><IconInfo /></Disc>
                    <Button size="sm" kind="gold" busy={actions.isBusy(`accept-${inv.gid}`)} onClick={() => void run(`accept-${inv.gid}`, () => api.guild.acceptInvite({ wid, vid: vid! }, inv.gid), `Welcome to ${inv.gname}!`)}>Accept</Button>
                    <Button size="sm" kind="steel" busy={actions.isBusy(`decline-${inv.gid}`)} onClick={() => void run(`decline-${inv.gid}`, () => api.guild.declineInvite({ wid, vid: vid! }, inv.gid), `You declined ${inv.gname}.`)}>Decline</Button>
                  </div>
                </Row>
              ))}
            </div>
          </Card>
          <Card>
            <CardHead title="Your requests" aside={limits.sendRequests ? `${requests.length} of ${limits.sendRequests}` : plural(requests.length, "request")} />
            {!requests.length && <Empty>You haven't asked any guild yet.</Empty>}
            <div className="rows">
              {requests.map((r) => (
                <Row key={r.gid}>
                  <Medallion src={art.social.crest} alt="" size={56} />
                  <RowText line={<span className="gd-name"><span className="nm">{r.gname}</span></span>} meta={<><b className="gold">[{r.gtag}]</b> · waiting on their answer</>} />
                  <div className="gd-acts">
                    <Button size="sm" kind="steel" busy={actions.isBusy(`cancel-${r.gid}`)} onClick={() => void run(`cancel-${r.gid}`, () => api.guild.removeRequest({ wid, vid: vid! }, r.gid), `Request to ${r.gname} withdrawn.`)}>Cancel</Button>
                  </div>
                </Row>
              ))}
            </div>
          </Card>
        </>
      )}

      {tab === "create" && <CreateGuild />}

      <GuildPeek wid={wid} vid={vid} target={peek} onClose={() => setPeek(null)} requested={requested} />
    </div>
  );
}

function CreateGuild() {
  const { wid, vid, actions } = useWorldCity();
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [touched, setTouched] = useState(false);
  const errors = validateGuildForm(name, tag, description);
  const ok = !errors.name && !errors.tag && !errors.description;
  return (
    <Card>
      <CardHead title="Found a guild" />
      <p className="gd-tagline">You become its Warlord. The tag is what the map and the diplomacy lists show — short, lowercase, letters and digits.</p>
      <form className="gd-form" onSubmit={(e) => { e.preventDefault(); setTouched(true); if (!ok) return; void actions.run("create", () => api.guild.create({ wid, vid: vid! }, name.trim(), tag.trim().toLowerCase(), description.trim()), { success: `${name.trim()} is founded. Long may it stand.` }); }}>
        <Field label="Name" value={name} maxLength={20} placeholder="The Purgatorists" onChange={(e) => setName(e.target.value)} error={touched ? errors.name : undefined} hint={`${name.trim().length} / 20`} />
        <Field label="Tag" value={tag} maxLength={6} placeholder="nwse" autoCapitalize="none" autoComplete="off" onChange={(e) => setTag(e.target.value.toLowerCase())} error={touched ? errors.tag : undefined} hint="1–6 lowercase letters or digits" />
        <div className="field">
          <label htmlFor="gd-desc">Description</label>
          <textarea id="gd-desc" className="input" value={description} maxLength={200} placeholder="Welcome to W2" onChange={(e) => setDescription(e.target.value)} />
          <div className={touched && errors.description ? "hint err" : "hint"}>{touched && errors.description ? errors.description : `${description.trim().length} / 200`}</div>
        </div>
        <Button type="submit" kind="crimson" wide busy={actions.isBusy("create")} disabled={touched && !ok}>Found the guild</Button>
      </form>
    </Card>
  );
}
